import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { api, config, copy } from './sf-practical-service.js';

const number = value => new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 }).format(value);
const text = (key, values = {}) => Object.entries(values).reduce((value, [name, replacement]) => value.replaceAll('{' + name + '}', replacement), copy[key] || '');
const element = (tag, className, content) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (content) node.textContent = content;
  return node;
};

function booking(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value.arrival_date)) throw new Error('BOOKING_INVALID');
  const day = new Date(value.arrival_date + 'T12:00:00Z');
  if (!Number.isFinite(day.getTime()) || day.toISOString().slice(0, 10) !== value.arrival_date) throw new Error('BOOKING_INVALID');
  const paid = Number(value.paid_boxes), weight = Number(value.weight_g), remaining = Number(value.paid_space_left_g);
  if (!Number.isSafeInteger(paid) || paid < 1 || paid > 1000 || !Number.isSafeInteger(weight) || weight < 0 || !Number.isSafeInteger(remaining) || remaining !== Math.max(0, paid * 6000 - weight)) throw new Error('BOOKING_INVALID');
  const manage = new URL(value.manage_url);
  if (manage.origin !== new URL(config.api).origin || manage.pathname !== '/store-b/practical/manage' || manage.username || manage.password || !manage.hash.startsWith('#token=')) throw new Error('BOOKING_INVALID');
  return { id:String(value.shipment_id||''), day, paid, weight, remaining, manage: manage.href, order: String(value.order_name || '') };
}

class AccountDeliveries extends HTMLElement {
  connectedCallback() {
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    for (const name of ['focus', 'pageshow', 'online']) window.addEventListener(name, this.refresh, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.cartChanged, options);
    document.addEventListener(ThemeEvents.cartSectionRestored, this.refresh, options);
    document.addEventListener('sf:arrival:revalidate', this.refresh, options);
    document.addEventListener('sf:delivery:changed', this.refresh, options);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
    this.refresh();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.request?.abort();
    clearTimeout(this.timer);
  }

  cartChanged = event => {
    this.request?.abort();
    this.setAttribute('aria-busy', 'true');
    Promise.resolve(event.promise).catch(() => {}).finally(() => { if (this.isConnected) this.refresh(); });
  };

  displayCacheKey() {
    const customer=String(config.cart_snapshot_key||'').split('|')[0];
    return customer&&this.hasAttribute('data-inline-calendar')?'sf-account-display-v1:'+config.shop+':'+config.account_link+':'+customer:null;
  }
  restoreDisplay() {
    const key=this.displayCacheKey();if(!key)return false;
    try {
      const saved=JSON.parse(sessionStorage.getItem(key)||'null');
      if(!saved||Date.now()-saved.at>120000||!Array.isArray(saved.deliveries)||!saved.deliveries.length)return false;
      const deliveries=saved.deliveries.map(d=>({...d,day:new Date(d.day)}));
      if(deliveries.some(d=>!Number.isFinite(d.day.getTime())||!Number.isSafeInteger(d.paid)||d.paid<1||typeof d.order!=='string'))return false;
      this.replaceChildren();this.hidden=false;this.render(deliveries);return true;
    } catch {return false;}
  }
  saveDisplay(deliveries) {
    const key=this.displayCacheKey();if(!key)return;
    try {
      if(!deliveries.length){sessionStorage.removeItem(key);return;}
      // Cache display fields only; signed manage/add tokens are never stored.
      sessionStorage.setItem(key,JSON.stringify({at:Date.now(),deliveries:deliveries.map(({id,day,paid,order,changesClosed})=>({id,day:day.toISOString(),paid,order,changesClosed}))}));
    } catch {}
  }

  refresh = async () => {
    if (!this.isConnected) return;
    this.request?.abort();
    clearTimeout(this.timer);
    const request = this.request = new AbortController();
    const previousHeight = this.getBoundingClientRect().height;
    if (previousHeight > 0) this.style.minHeight = previousHeight + 'px';
    this.setAttribute('aria-busy', 'true');
    if (!this.settled&&this.restoreDisplay())this.settled=true;
    if (!this.settled) {
      this.hidden = false;
      this.replaceChildren(element('p', 'sf-account-status', text('account_checking')));
    }
    try {
      const {response,value} = await (config.accountLookup ? config.accountLookup() : (async()=>{
      const response = await fetch(config.account_link, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
        signal: AbortSignal.any([request.signal, AbortSignal.timeout(12000)])
      });
      const value = await response.json();
        return {response,value};
      })());
      if (request !== this.request || request.signal.aborted || !this.isConnected) return;
      if (response.status === 401 && value?.error === 'ACCOUNT_SIGN_IN_REQUIRED') {
        this.saveDisplay([]);
        this.replaceChildren();
        this.hidden = false;
        delete this.dataset.hasDeliveries;
        const link = element('a', 'sf-account-sign-in', text('account_sign_in'));
        link.href = config.account_login;
        this.append(link);
      } else {
        if (!response.ok || value?.authenticated !== true || !Array.isArray(value.deliveries)) throw new Error('LOOKUP_FAILED');
        const deliveries = await Promise.all(value.deliveries.map(async value => {
          const delivery = booking(value);
          try {
            const token = new URLSearchParams(new URL(delivery.manage).hash.slice(1)).get('token');
            const view = await api('/practical/manage/view', { token });
            const now = Date.parse(view.server_now), cutoff = Date.parse(view.cutoff);
            delivery.changesClosed = Number.isFinite(now) && Number.isFinite(cutoff) && now >= cutoff;
          } catch {}
          return delivery;
        }));
        if (request !== this.request || request.signal.aborted || !this.isConnected) return;
        this.replaceChildren();
        this.hidden = false;
        delete this.dataset.hasDeliveries;
        this.saveDisplay(deliveries);
        if (!deliveries.length) this.hidden = true;
        else this.render(deliveries);
      }
    } catch {
      if (request !== this.request || request.signal.aborted || !this.isConnected) return;
      delete this.dataset.hasDeliveries;
      this.hidden = false;
      this.replaceChildren(element('p', 'sf-account-status', text('account_unavailable')));
      const retry = element('button', 'button button-secondary', text('retry'));
      retry.type = 'button';
      retry.addEventListener('click', this.refresh, { signal: this.events.signal });
      this.append(retry);
    } finally {
      if (request === this.request && this.isConnected) {
        this.settled = true;
        this.setAttribute('aria-busy', 'false');
        this.style.removeProperty('min-height');
        window.SfDeliveryLayout?.remember('account',this);
        this.timer = setTimeout(() => { if (!document.hidden) this.refresh(); }, 60000);
      }
    }
  };

  render(deliveries) {
    this.dataset.hasDeliveries = 'true';
    this.append(element('h2', 'sf-account-heading', text('account_heading')));
    const list = element('div', 'sf-account-list');
    for (const delivery of deliveries) {
      const card = element('section', 'sf-account-card');
      const date = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(delivery.day);
      card.append(element('p', 'sf-account-order', delivery.order));
      card.append(element('h3', 'sf-account-date', date));
      card.append(element('p', 'sf-account-paid', text(delivery.paid === 1 ? 'account_paid_one' : 'account_paid_many', { count: number(delivery.paid) })));
      if (delivery.changesClosed) card.append(element('p', 'sf-account-note', 'Changes closed — cutoff passed'));
      const manage = element('a', 'sf-account-manage', text('account_manage'));
      manage.href = delivery.manage;
      manage.referrerPolicy = 'no-referrer';
      if(!this.hasAttribute('data-inline-calendar'))card.append(manage);
      list.append(card);
    }
    this.append(list);
  }
}

// The bootstrap controls feature loading. Theme IDs change when a theme is
// copied or published; tying registration to old IDs strands the loading panel.
if (config?.shop === 'signature-frags-9190.myshopify.com' && config.account_link) {
  if (!customElements.get('sf-account-deliveries')) customElements.define('sf-account-deliveries', AccountDeliveries);
  const mount = () => {
    const page = document.querySelector('.cart-page.sf-basket-layout');
    if (!page || page.querySelector('sf-account-deliveries')) return;
    const panel = document.createElement('sf-account-deliveries');
    panel.setAttribute('aria-live', 'polite');
    panel.setAttribute('data-inline-calendar','');
    panel.hidden = true;
    const planner = page.querySelector('.cart-summary__inner sf-arrival-picker:not([data-compact])');
    const items = page.querySelector('.cart-page__items');
    if (planner) planner.before(panel);
    else if (items) items.after(panel);
    else page.append(panel);
  };
  new MutationObserver(mount).observe(document.body, { childList: true, subtree: true });
  mount();
}





